import { FC } from 'react';
import { RESULT_INSUFFICIENT_REASON_LABEL } from 'src/features/proofs';
import { ProcessStatus } from '../../hooks/useOfferLetterChecklist';
import { OfferLetterReasonChip } from '../../styles';
import { ActionBarContainer, ActionButton, SubmitVerificationButton } from './styles';

interface OfferLetterActionBarProps {
  processStatus: ProcessStatus;
  autoMappedReasonCodes: string[];
  softReasonCodes?: string[];
  isAllComplete: boolean;
  onMarkEdited: () => void;
  onMarkInsufficient: (reasonCodes: string[]) => void;
  onSubmitVerification?: () => void;
  isSubmitting: boolean;
}

const ReasonChips: FC<{ codes: string[] }> = ({ codes }) => (
  <div data-testid="olv-reason-chips">
    {codes.map((code) => (
      <OfferLetterReasonChip key={code}>
        {RESULT_INSUFFICIENT_REASON_LABEL[code as keyof typeof RESULT_INSUFFICIENT_REASON_LABEL] ||
          code}
      </OfferLetterReasonChip>
    ))}
  </div>
);

const OfferLetterActionBar: FC<OfferLetterActionBarProps> = ({
  processStatus,
  autoMappedReasonCodes,
  softReasonCodes = [],
  isAllComplete,
  onMarkEdited,
  onMarkInsufficient,
  onSubmitVerification,
  isSubmitting
}) => {
  if (processStatus === 'stopped_edited') {
    return (
      <ActionBarContainer>
        <ActionButton
          onClick={onMarkEdited}
          disabled={isSubmitting}
          data-testid="olv-mark-edited-btn"
        >
          {isSubmitting ? 'Submitting...' : 'Mark as Failed'}
        </ActionButton>
      </ActionBarContainer>
    );
  }

  if (processStatus === 'stopped_insufficient') {
    return (
      <ActionBarContainer>
        <ReasonChips codes={autoMappedReasonCodes} />
        <ActionButton
          onClick={() => onMarkInsufficient(autoMappedReasonCodes)}
          disabled={isSubmitting || autoMappedReasonCodes.length === 0}
          data-testid="olv-mark-insufficient-btn"
        >
          {isSubmitting ? 'Submitting...' : 'Mark Insufficient'}
        </ActionButton>
      </ActionBarContainer>
    );
  }

  // processStatus === 'active'
  if (!isAllComplete) return null;

  if (autoMappedReasonCodes.length > 0) {
    return (
      <ActionBarContainer>
        <ReasonChips codes={autoMappedReasonCodes} />
        <ActionButton
          onClick={() => onMarkInsufficient(autoMappedReasonCodes)}
          disabled={isSubmitting}
          data-testid="olv-mark-insufficient-btn"
        >
          {isSubmitting ? 'Submitting...' : 'Mark Insufficient'}
        </ActionButton>
      </ActionBarContainer>
    );
  }

  // All steps passed — submit for employer verification (soft reason codes shown as info, not blocking)
  return (
    <ActionBarContainer>
      {softReasonCodes.length > 0 && <ReasonChips codes={softReasonCodes} />}
      <SubmitVerificationButton
        onClick={onSubmitVerification}
        disabled={isSubmitting}
        data-testid="olv-submit-verification-btn"
      >
        {isSubmitting ? 'Submitting...' : 'Submit for Employer Verification'}
      </SubmitVerificationButton>
    </ActionBarContainer>
  );
};

export default OfferLetterActionBar;
